import { DoubleSide, Object3D, Vector3 } from 'three';
import { Point3D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { H3BasicMaterial } from '../../../materials/basic/h3-basic-material';
import { PointCloudContentState } from '../../../state/point-cloud-content-state';
import { RendererLayer, setRendererLayers } from '../../render-utils';
import {
  AbstractMeshHelper,
  ConfigureMeshesConfiguration,
} from '../abstract-mesh-helper';
import { MeasurementColors } from '../color-utils';
import {
  getOutlinedSphereMaterialParameters,
  OutlinedSphere,
} from '../outlined-sphere';
import { PoleMesh } from '../pole-mesh';

export class MeasurementPoleHelper extends AbstractMeshHelper {
  readonly circleRadius = 0.3;
  readonly poleMeshRadius = 0.04;
  readonly poleHeight = 4;

  private measurementPole = new Object3D();

  private poleMesh: PoleMesh;
  private outlinedSphere: OutlinedSphere;

  constructor(private name: 'reference' | 'target') {
    super();
    this.poleMesh = new PoleMesh(
      new Vector3(),
      new Vector3(0, 0, this.poleHeight),
      this.poleMeshRadius,
    );

    this.outlinedSphere = new OutlinedSphere(this.circleRadius);

    this.measurementPole.add(this.poleMesh, this.outlinedSphere);
    this.measurementPole.userData = { meshType: 'MeasurementPole' };
    this.measurementPole.name = name;
  }

  getMeshes() {
    return [this.measurementPole];
  }

  dispose() {
    this.measurementPole.remove(this.outlinedSphere, this.poleMesh);
    this.outlinedSphere.dispose();
    this.poleMesh.dispose();
  }

  getPosition() {
    return this.measurementPole.position;
  }

  updatePosition(position: Point3D) {
    this.measurementPole.position.fromArray(position);
  }

  protected override updateRendererLayers(_state: PointCloudContentState) {
    setRendererLayers([RendererLayer.Main], this.measurementPole);
  }

  protected override configureMeshes({
    state,
    materialService,
  }: ConfigureMeshesConfiguration) {
    const shouldBeHighlighted =
      this.name === 'target' && !!state.render.freePointMeasurement?.active;

    const color = shouldBeHighlighted
      ? MeasurementColors.HighLight
      : MeasurementColors.Standard;

    this.poleMesh.setMaterial(
      materialService.get(H3BasicMaterial, { color, side: DoubleSide }),
    );

    const materialsParameters = getOutlinedSphereMaterialParameters({
      color,
      opacity: 0.4,
    });
    this.outlinedSphere.setMaterial(
      materialService.get(H3BasicMaterial, materialsParameters.sphere),
    );
  }
}
